import React, { useRef, useEffect, useState } from 'react';
import { FiX, FiMinus, FiPlus, FiRotateCw } from 'react-icons/fi';
import './PaintCanvas.css';

function PaintCanvas({ imageSrc, onMaskGenerated, onClose, mode = 'floor' }) {
  const canvasRef = useRef(null);
  const maskCanvasRef = useRef(null); // Separate canvas for tracking mask
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushSize, setBrushSize] = useState(20);
  const [showInstructions, setShowInstructions] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  const originalImageRef = useRef(null);

  // Load and draw the original image
  useEffect(() => {
    if (!imageSrc || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const maskCanvas = maskCanvasRef.current;
    const maskCtx = maskCanvas ? maskCanvas.getContext('2d') : null;
    const img = new Image();
    
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      // Set canvas size to match image
      canvas.width = img.width;
      canvas.height = img.height;
      
      if (maskCanvas) {
        maskCanvas.width = img.width;
        maskCanvas.height = img.height;
        // Initialize mask as all black (nothing selected)
        if (maskCtx) {
          maskCtx.fillStyle = 'black';
          maskCtx.fillRect(0, 0, maskCanvas.width, maskCanvas.height);
        }
      }
      
      // Draw original image
      ctx.drawImage(img, 0, 0);
      
      // Store reference
      originalImageRef.current = img;
      setImageLoaded(true);
    };
    
    img.src = imageSrc;
  }, [imageSrc]);

  // Get mouse/touch position relative to canvas
  const getEventPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      // Touch event
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    } else {
      // Mouse event
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY
      };
    }
  };

  // Start drawing
  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const pos = getEventPos(e);
    drawAt(pos.x, pos.y);
  };

  // Draw at position
  const drawAt = (x, y) => {
    const canvas = canvasRef.current;
    const maskCanvas = maskCanvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const maskCtx = maskCanvas ? maskCanvas.getContext('2d') : null;
    
    // Draw semi-transparent white overlay on main canvas for visual feedback
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.beginPath();
    ctx.arc(x, y, brushSize / 2, 0, Math.PI * 2);
    ctx.fill();
    
    // Draw white on mask canvas (white = selected area)
    if (maskCtx) {
      maskCtx.globalCompositeOperation = 'source-over';
      maskCtx.fillStyle = 'white';
      maskCtx.beginPath();
      maskCtx.arc(x, y, brushSize / 2, 0, Math.PI * 2);
      maskCtx.fill();
    }
  };

  // Continue drawing
  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const pos = getEventPos(e);
    drawAt(pos.x, pos.y);
  };

  // Stop drawing
  const stopDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(false);
  };

  // Clear canvas and redraw original image
  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const maskCanvas = maskCanvasRef.current;
    if (!canvas || !originalImageRef.current) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(originalImageRef.current, 0, 0);
    
    // Reset mask canvas
    if (maskCanvas) {
      const maskCtx = maskCanvas.getContext('2d');
      maskCtx.fillStyle = 'black';
      maskCtx.fillRect(0, 0, maskCanvas.width, maskCanvas.height);
    }
  };

  // Generate mask from painted areas
  const generateMask = () => {
    const maskCanvas = maskCanvasRef.current;
    if (!maskCanvas) return;

    // Convert mask canvas to blob and call callback
    maskCanvas.toBlob((blob) => {
      if (blob && onMaskGenerated) {
        onMaskGenerated(blob);
      }
    }, 'image/png');
  };

  const increaseBrushSize = () => {
    setBrushSize(prev => Math.min(prev + 5, 100));
  };

  const decreaseBrushSize = () => {
    setBrushSize(prev => Math.max(prev - 5, 5));
  };

  if (!imageSrc) return null;

  return (
    <div className="paint-canvas-overlay">
      <div className="paint-canvas-container">
        <div className="paint-canvas-header">
          <h3>
            {mode === 'floor' && 'Paint Floor Areas'}
            {mode === 'paint' && 'Paint Areas to Paint'}
            {mode === 'replace' && 'Paint Object to Replace'}
          </h3>
          <button className="paint-close-btn" onClick={onClose}>
            <FiX />
          </button>
        </div>

        {showInstructions && (
          <div className="paint-instructions">
            <p>
              <strong>How to use:</strong> Paint over the {
                mode === 'floor' ? 'floor areas you want to restyle' :
                mode === 'paint' ? 'areas you want to paint' :
                'object you want to replace'
              }.
              The white overlay shows where you've painted. Use the +/- buttons to adjust brush size.
            </p>
            <button onClick={() => setShowInstructions(false)}>Got it</button>
          </div>
        )}

        <div className="paint-toolbar">
          <div className="brush-controls">
            <label>Brush Size: {brushSize}px</label>
            <div className="brush-size-buttons">
              <button onClick={decreaseBrushSize} className="brush-size-btn">
                <FiMinus />
              </button>
              <button onClick={increaseBrushSize} className="brush-size-btn">
                <FiPlus />
              </button>
            </div>
          </div>
          <button onClick={clearCanvas} className="clear-btn">
            <FiRotateCw /> Clear
          </button>
        </div>

        <div className="paint-canvas-wrapper">
          {!imageLoaded && <div className="paint-loading">Loading image...</div>}
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <canvas
              ref={canvasRef}
              className="paint-canvas"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
              style={{ cursor: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="${brushSize}" height="${brushSize}"><circle cx="${brushSize/2}" cy="${brushSize/2}" r="${brushSize/2-1}" fill="none" stroke="white" stroke-width="2"/></svg>') ${brushSize/2} ${brushSize/2}, crosshair` }}
            />
            {/* Hidden canvas for mask tracking */}
            <canvas
              ref={maskCanvasRef}
              style={{ display: 'none' }}
            />
          </div>
        </div>

        <div className="paint-actions">
          <button onClick={onClose} className="paint-cancel-btn">
            Cancel
          </button>
          <button onClick={generateMask} className="paint-save-btn">
            Save Mask & Continue
          </button>
        </div>
      </div>
    </div>
  );
}

export default PaintCanvas;

